import { styled, alpha } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import type { TypographyProps } from '@mui/material/Typography';

export const StepFormRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  width: '100%',
  maxWidth: '100%',
}));

/** Título de sección sin caja alrededor */
export const ComparisonSectionTitle = styled(Typography)<TypographyProps>(({ theme }) => ({
  fontSize: '0.8125rem',
  fontWeight: 600,
  letterSpacing: '-0.01em',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1.25),
}));

export const ComparisonStatsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: theme.spacing(1),
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  },
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  },
}));

/**
 * Solo color de fondo suave del acento — sin borde ni sombra oscura.
 */
export const ComparisonStatCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$accent',
})<{ $accent: string }>(({ theme, $accent }) => ({
  position: 'relative',
  padding: theme.spacing(1.35, 1.5, 2.25),
  borderRadius: 12,
  backgroundColor: theme.palette.mode === 'dark' ? alpha($accent, 0.18) : alpha($accent, 0.11),
  border: 'none',
  boxShadow: 'none',
}));

export const ComparisonStatLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.6875rem',
  fontWeight: 600,
  letterSpacing: '0.035em',
  textTransform: 'uppercase',
  lineHeight: 1.35,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.35),
}));

export const ComparisonStatValue = styled(Typography, {
  shouldForwardProp: (prop) => prop !== '$accent',
})<{ $accent: string }>(({ $accent }) => ({
  fontSize: '1.3125rem',
  fontWeight: 700,
  lineHeight: 1.1,
  letterSpacing: '-0.03em',
  fontVariantNumeric: 'tabular-nums',
  color: $accent,
}));

/** Un solo bloque pequeño para el resumen: sin borde, solo fondo muy tenue */
export const SessionSummaryCompact = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 1.75),
  borderRadius: 10,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.045)
      : alpha(theme.palette.primary.main, 0.055),
  border: 'none',
  boxShadow: 'none',
}));

export const SessionSummaryTitle = styled(Typography)<TypographyProps>(({ theme }) => ({
  fontSize: '0.6875rem',
  fontWeight: 600,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1),
}));

export const SessionSummaryGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  columnGap: theme.spacing(2.5),
  rowGap: theme.spacing(1.15),
  [theme.breakpoints.down(480)]: {
    gridTemplateColumns: '1fr',
  },
}));

export const SessionSummaryCell = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.25),
  minWidth: 0,
}));

export const SessionSummaryMetaLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.6875rem',
  fontWeight: 500,
  letterSpacing: '0.02em',
  color: theme.palette.text.secondary,
  lineHeight: 1.2,
}));

export const SessionSummaryMetaValue = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  fontWeight: 600,
  lineHeight: 1.35,
  color: theme.palette.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));
